import React, { useState, useCallback, useEffect, Suspense, lazy } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import ColorPicker from './components/ColorPicker';
import MoodPanel from './components/MoodPanel';
import NavTabs from './components/NavTabs';
import StructuredData from './components/StructuredData';
import ColorOfMonth from './components/ColorOfMonth';
import EnterpriseModal from './components/EnterpriseModal';
import Sitemap from './components/Sitemap';
import ToolIntroduction from './components/ToolIntroduction';
import { analyzeColor } from './utils/colorLogic';
import { colord } from 'colord';

const BrandFit = lazy(() => import('./components/BrandFit'));
const Accessibility = lazy(() => import('./components/Accessibility'));
const PersonalityMatch = lazy(() => import('./components/PersonalityMatch'));
const BrandRiskMeter = lazy(() => import('./components/BrandRiskMeter'));
const InteractiveHeatMap = lazy(() => import('./components/InteractiveHeatMap'));
const BrandSimulation = lazy(() => import('./components/BrandSimulation'));
const ConversionColorEngine = lazy(() => import('./components/ConversionColorEngine'));
const SwatchExplorer = lazy(() => import('./components/SwatchExplorer'));
const IndustryGuide = lazy(() => import('./components/IndustryGuide'));
const EmotionGallery = lazy(() => import('./components/EmotionGallery'));
const SEOContent = lazy(() => import('./components/SEOContent'));
const DesignTools = lazy(() => import('./components/DesignTools'));
const SharePalette = lazy(() => import('./components/SharePalette'));
const SnippetGenerator = lazy(() => import('./components/SnippetGenerator'));
const Instructions = lazy(() => import('./components/Instructions'));
const EcommerceStats = lazy(() => import('./components/EcommerceStats'));
const SocialShareBottom = lazy(() => import('./components/SocialShareBottom'));

const LoadingFallback = () => (
  <div className="w-full h-32 sm:h-40 md:h-48 glass-panel rounded-xl sm:rounded-2xl md:rounded-3xl flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 border-2 sm:border-3 border-blue-400/30 border-t-blue-400 rounded-full animate-spin" />
      <span className="text-[10px] sm:text-xs font-light uppercase tracking-[0.15em] sm:tracking-[0.2em] text-blue-300/60 font-display">
        Loading...
      </span>
    </div>
  </div>
);

function App() {
  const [color, setColor] = useState('#3b82f6');
  const [isEnterpriseOpen, setIsEnterpriseOpen] = useState(false);
  const { mood } = analyzeColor(color);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlColor = params.get('color');
    if (urlColor && /^#[0-9A-F]{6}$/i.test(urlColor)) {
      setColor(urlColor);
    }
  }, []);

  const generateDynamicBackground = useCallback((baseColor) => {
    const c = colord(baseColor);
    const { h, s, l } = c.toHsl();
    const darkBase = '#03173a';
    const gradientTop = colord({ h, s: Math.min(s + 20, 100), l: Math.max(l - 30, 15) }).toHex();
    const gradientBottom = '#03173a';
    return { darkBase, gradientTop, gradientBottom };
  }, []);

  const handleColorChange = useCallback((newColor) => {
    if (newColor && newColor.startsWith('#')) {
      setColor(newColor);
      const newUrl = `${window.location.origin}${window.location.pathname}?color=${encodeURIComponent(newColor)}`;
      window.history.replaceState({ path: newUrl }, '', newUrl);
    }
  }, []);

  const dynamicBg = generateDynamicBackground(color);

  return (
    <div
      className="min-h-screen relative selection:bg-blue-500 selection:text-white pb-8 sm:pb-12 md:pb-16 lg:pb-20 transition-colors duration-1000 overflow-x-hidden"
      style={{
        background: `radial-gradient(circle at 50% -20%, ${dynamicBg.gradientTop} 0%, ${dynamicBg.darkBase} 50%, ${dynamicBg.gradientBottom} 100%)`
      }}
    >
      <StructuredData color={color} mood={mood} />

      <div
        className="premium-glow top-[-10%] left-[-5%] w-[60%] h-[60%] transition-colors duration-1000"
        style={{ backgroundColor: color, opacity: 0.12 }}
      />
      <div
        className="premium-glow bottom-[-10%] right-[-5%] w-[50%] h-[50%] transition-colors duration-1000"
        style={{ backgroundColor: colord(color).rotate(40).toHex(), opacity: 0.08 }}
      />

      <div
        className="fixed inset-0 pointer-events-none z-[100]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          opacity: 0.02
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4 sm:py-6 md:py-8 lg:py-10 relative z-10">
        {/* Header */}
        <header className="mb-6 sm:mb-8 md:mb-10 lg:mb-12 flex items-center justify-between gap-3 sm:gap-4 md:gap-6">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 min-w-0">
            <div
              className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-lg sm:rounded-xl md:rounded-2xl p-[2px] shadow-2xl transition-all duration-500 flex-shrink-0"
              style={{
                background: `linear-gradient(135deg, ${color} 0%, ${colord(color).darken(0.3).toHex()} 100%)`
              }}
            >
              <div className="w-full h-full rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center transition-colors duration-500 bg-[#03173a]">
                <SafeIcon icon={FiIcons.FiZap} className="text-white text-lg sm:text-xl md:text-2xl lg:text-3xl" />
              </div>
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-light tracking-tight font-display leading-none text-white truncate">
                COLOR INTELLIGENCE
              </h1>
              <p
                className="text-[8px] sm:text-[9px] md:text-[10px] lg:text-xs font-light uppercase tracking-[0.1em] sm:tracking-[0.12em] mt-1 sm:mt-1.5 md:mt-2 transition-colors duration-500 truncate font-display"
                style={{ color: colord(color).alpha(0.7).toRgbString() }}
              >
                AI-Powered Brand Analysis
              </p>
            </div>
          </div>
        </header>

        <NavTabs color={color} />

        <main className="space-y-8 sm:space-y-10 md:space-y-12 lg:space-y-16">
          {/* Intelligence Section - NEW LAYOUT */}
          <section id="intelligence" className="scroll-mt-16 sm:scroll-mt-20 md:scroll-mt-24">
            {/* Tool Introduction - NEW */}
            <ToolIntroduction />

            <div className="grid lg:grid-cols-12 gap-4 sm:gap-6 md:gap-8">
              {/* Color Picker - Left Side */}
              <aside className="lg:col-span-4">
                <ColorPicker color={color} onChange={handleColorChange} />
              </aside>

              {/* Mood Panel - Right Side */}
              <div className="lg:col-span-8">
                <MoodPanel color={color} />
              </div>
            </div>
          </section>

          {/* Workspace Section */}
          <div id="workspace" className="grid lg:grid-cols-12 gap-4 sm:gap-6 md:gap-8 items-start scroll-mt-16 sm:scroll-mt-20 md:scroll-mt-24">
            <aside className="lg:col-span-4 space-y-4 sm:space-y-5 md:space-y-6">
              <Suspense fallback={<LoadingFallback />}>
                <SharePalette color={color} />
                <DesignTools color={color} onSelectColor={handleColorChange} />
                <SnippetGenerator color={color} />
              </Suspense>
            </aside>

            <div className="lg:col-span-8 space-y-4 sm:space-y-5 md:space-y-6">
              <Suspense fallback={<LoadingFallback />}>
                <div className="grid md:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
                  <BrandFit color={color} />
                  <Accessibility color={color} />
                </div>
                <BrandRiskMeter color={color} />
                <ConversionColorEngine color={color} />
                <BrandSimulation color={color} />
                <InteractiveHeatMap color={color} onColorChange={handleColorChange} />
                <div id="personality" className="scroll-mt-16 sm:scroll-mt-20 md:scroll-mt-24">
                  <PersonalityMatch color={color} />
                </div>
                <div id="discovery" className="grid sm:grid-cols-2 gap-4 sm:gap-5 md:gap-6 scroll-mt-16 sm:scroll-mt-20 md:scroll-mt-24">
                  <SwatchExplorer onSelectColor={handleColorChange} />
                  <IndustryGuide onSelectColor={handleColorChange} />
                </div>
              </Suspense>
            </div>
          </div>

          {/* Color of the Month */}
          <ColorOfMonth onSelectColor={handleColorChange} />

          {/* Additional Sections */}
          <Suspense fallback={<LoadingFallback />}>
            <Instructions />

            <section id="theory" className="space-y-8 sm:space-y-10 md:space-y-12 pt-8 sm:pt-10 md:pt-12 border-t border-white/[0.08] scroll-mt-16 sm:scroll-mt-20 md:scroll-mt-24">
              <EmotionGallery onSelectColor={handleColorChange} />
              <SEOContent />
            </section>

            <EcommerceStats />
            <Sitemap />
            <SocialShareBottom color={color} />
          </Suspense>
        </main>

        {/* Footer */}
        <footer className="mt-12 sm:mt-16 md:mt-20 lg:mt-24 pt-8 sm:pt-10 md:pt-12 border-t border-white/[0.08]">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div className="space-y-2 sm:space-y-3 text-center md:text-left">
              <h2 className="text-white text-base sm:text-lg md:text-xl lg:text-2xl font-light tracking-tight font-display">
                COLOR INTELLIGENCE
              </h2>
              <p className="text-[8px] sm:text-[9px] md:text-[10px] font-extralight text-white/40 uppercase tracking-[0.08em] sm:tracking-[0.1em] font-display">
                © 2026{' '}
                <a 
                  href="https://sheker.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-white/60 transition-colors underline decoration-white/20 hover:decoration-white/40"
                >
                  sheker.com
                </a>
              </p>
            </div>
            <nav className="flex flex-wrap justify-center gap-4 sm:gap-6 md:gap-8 text-[9px] sm:text-[10px] md:text-xs text-white/40 font-light tracking-[0.08em] sm:tracking-[0.1em] uppercase font-display">
              <a href="#" className="hover:text-white transition-colors">Privacy</a>
              <a href="#" className="hover:text-white transition-colors">Terms</a>
              <a href="#" className="hover:text-white transition-colors">API</a>
              <a href="#" className="hover:text-white transition-colors">Support</a>
            </nav>
          </div>
          <div className="flex justify-center pt-6 border-t border-white/[0.08]">
            <button
              onClick={() => setIsEnterpriseOpen(true)}
              className="px-6 sm:px-8 md:px-10 lg:px-12 py-2.5 sm:py-3 md:py-4 rounded-lg sm:rounded-xl md:rounded-2xl bg-white text-[9px] sm:text-[10px] md:text-xs font-medium uppercase tracking-[0.1em] sm:tracking-[0.12em] hover:bg-blue-50 transition-all shadow-2xl active:scale-95 font-display"
              style={{ color: dynamicBg.darkBase }}
            >
              Contact Us
            </button>
          </div>
        </footer>
      </div>

      <EnterpriseModal isOpen={isEnterpriseOpen} onClose={() => setIsEnterpriseOpen(false)} color={color} />
    </div>
  );
}

export default App;